import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { experimentationGetStarted } from '@gonfalon/dogfood-flags/dogfood-flags';
import { CopyToClipboard } from '@gonfalon/launchpad-experimental/CopyToClipboard';
import { Button, ButtonGroup, IconButton, ProgressBar, Text } from '@launchpad-ui/components';
import cx from 'clsx';
import Prism from 'prismjs';

// oxlint-disable-next-line import/no-unassigned-import
import './prismInit';

import type { SnippetLang } from '../../utils/types';

import styles from './SdkSetupSnippet.module.css';

type SdkSetupSnippetProps = {
  withCopyButton?: boolean;
  withLineNumbers?: boolean;
  withCommandLine?: boolean;
  expandable?: boolean;
  snippets: Array<{
    title: string;
    snippet: string;
    language: SnippetLang;
  }>;
  trackAnalyticsOnClick?: () => void;
  selectedSnippetTitle?: string;
  onSelectedSnippetTitleChange?: (title: string) => void;
  loading?: boolean;
};

const MAX_LINES_TO_EXPAND = 12;

export const SdkSetupSnippet: FC<SdkSetupSnippetProps> = ({
  snippets,
  withCopyButton,
  withLineNumbers,
  withCommandLine,
  expandable = experimentationGetStarted(),
  trackAnalyticsOnClick,
  selectedSnippetTitle: controlledSelectedSnippetTitle,
  onSelectedSnippetTitleChange,
  loading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedSnippetTitle, setSelectedSnippetTitle] = useState(snippets[0]?.title);

  const selectedSnippet = snippets.find((snippet: { title: string }) => snippet.title === selectedSnippetTitle);

  const isExpandable =
    expandable && (selectedSnippet?.snippet ?? '').trimEnd().split('\n').length > MAX_LINES_TO_EXPAND;

  const showCopyButton = withCopyButton && selectedSnippet?.snippet;

  const codeEl = useRef<HTMLElement>(null);

  const firstSnippetTitle = snippets[0]?.title;
  useEffect(() => {
    setSelectedSnippetTitle(firstSnippetTitle);
    onSelectedSnippetTitleChange?.(firstSnippetTitle);
  }, [firstSnippetTitle, onSelectedSnippetTitleChange]);

  useEffect(() => {
    if (controlledSelectedSnippetTitle) {
      setSelectedSnippetTitle(controlledSelectedSnippetTitle);
    }
  }, [controlledSelectedSnippetTitle]);

  useLayoutEffect(() => {
    const element = codeEl.current;
    if (!element) {
      return;
    }

    // Use requestAnimationFrame to ensure that the element is mounted
    // before highlighting it.
    const frame = requestAnimationFrame(() => {
      Prism.highlightElement(element);
    });

    // Cancel the animation frame when the component unmounts.
    return () => cancelAnimationFrame(frame);
  }, [selectedSnippet?.snippet, selectedSnippet?.language]);

  return (
    <div className={styles.snippetContainer}>
      <header className={styles.snippetHeader}>
        <div className={styles.snippetTitles}>
          {snippets.map((snippet: { title: string }) => (
            <Text
              onClick={() => {
                setSelectedSnippetTitle(snippet.title);
                onSelectedSnippetTitleChange?.(snippet.title);
              }}
              key={snippet.title}
              className={cx(styles.snippetTitle, {
                [styles.selectedSnippetTitle]: selectedSnippetTitle === snippet.title,
                [styles.snippetTitleMultiple]: snippets.length > 1,
              })}
            >
              {snippet.title}
            </Text>
          ))}
        </div>
        {(showCopyButton || isExpandable) && (
          <ButtonGroup>
            {isExpandable && (
              <Button onPress={() => setExpanded(!expanded)} variant="minimal" size="small">
                {expanded ? 'Show less' : 'Show all'}
              </Button>
            )}
            {showCopyButton && (
              <CopyToClipboard text={selectedSnippet.snippet.trimEnd()} onCopy={trackAnalyticsOnClick}>
                <IconButton aria-label="Copy code snippet" variant="minimal" icon="copy-code" isDisabled={loading} />
              </CopyToClipboard>
            )}
          </ButtonGroup>
        )}
      </header>

      <div className={cx(styles.snippet, { [styles.expandable]: isExpandable && !expanded })}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <ProgressBar aria-label="Loading SDK keys" isIndeterminate size="large" />
          </div>
        )}
        <pre
          className={cx({
            'line-numbers': withLineNumbers,
            'command-line': withCommandLine,
          })}
          data-start={1}
          data-line-offset={0}
          data-prompt={withCommandLine ? '$' : undefined}
        >
          <code className={`language-${selectedSnippet?.language}`} ref={codeEl}>
            {selectedSnippet?.snippet.trimEnd()}
          </code>
        </pre>
      </div>
    </div>
  );
};
